/**
 * AddonDiagnosticsTab - Tab de diagnóstico de problemas
 * Exibe KPIs e lista de inconsistências detectadas com ações de correção
 * 
 * @version 1.1
 * @date 2025-12-19
 * @changelog
 *   - v1.1: Adicionados botões de ação para correção de problemas
 */

import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  XCircle,
  AlertCircle,
  Plus,
  Trash2,
  RefreshCw
} from 'lucide-react';

/**
 * Interface para diagnóstico
 */
interface AddonDiagnostics {
  addons_without_stripe: number;
  orphan_instances: number;
  cancelled_with_active_instances: number;
  total_issues: number;
  checked_at: string;
}

interface AddonDiagnosticsTabProps {
  diagnostics?: AddonDiagnostics;
  isLoading: boolean;
  onCreateManual?: () => void;
  onRefreshData?: () => void;
}

/**
 * Card de diagnóstico individual com ação
 */
function DiagnosticCard({ 
  title, 
  description, 
  count, 
  severity,
  icon: Icon,
  actionLabel,
  actionIcon: ActionIcon,
  onAction
}: { 
  title: string; 
  description: string; 
  count: number; 
  severity: 'success' | 'warning' | 'error';
  icon: React.ElementType;
  actionLabel?: string;
  actionIcon?: React.ElementType;
  onAction?: () => void;
}) {
  const severityColors = {
    success: 'text-emerald-500 bg-emerald-500/10',
    warning: 'text-amber-500 bg-amber-500/10',
    error: 'text-red-500 bg-red-500/10',
  };

  const badgeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
    success: 'secondary',
    warning: 'outline',
    error: 'destructive',
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className={`p-2 rounded-lg ${severityColors[severity]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <Badge variant={badgeVariants[severity]}>
            {count} {count === 1 ? 'ocorrência' : 'ocorrências'}
          </Badge>
        </div>
        <CardTitle className="text-base mt-2">{title}</CardTitle>
        <CardDescription className="text-sm">{description}</CardDescription>
      </CardHeader>
      {/* Botão de ação quando há problemas */}
      {count > 0 && actionLabel && onAction && (
        <CardContent className="pt-0">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={onAction}
          >
            {ActionIcon && <ActionIcon className="h-4 w-4 mr-2" />}
            {actionLabel}
          </Button>
        </CardContent>
      )}
    </Card>
  );
}

export function AddonDiagnosticsTab({ 
  diagnostics, 
  isLoading,
  onCreateManual,
  onRefreshData
}: AddonDiagnosticsTabProps) {
  const { t } = useTranslation('admin');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center text-muted-foreground">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-pulse" />
          <p>Carregando diagnóstico...</p>
        </div>
      </div>
    );
  }

  const totalIssues = diagnostics?.total_issues ?? 0;

  /**
   * Handler para abrir cadastro manual (add-ons sem Stripe)
   */
  const handleAddonsWithoutStripe = () => {
    if (onCreateManual) {
      onCreateManual();
    }
  };

  /**
   * Handler para instâncias órfãs
   * Navega para a listagem com filtro de pendentes
   */
  const handleOrphanInstances = () => {
    if (onRefreshData) {
      onRefreshData();
    }
    // TODO: Implementar navegação para tab de listagem com filtro
  };

  /**
   * Handler para cancelados com instâncias ativas
   */
  const handleCancelledWithActive = () => {
    if (onRefreshData) {
      onRefreshData();
    }
    // TODO: Implementar navegação para tab de listagem com filtro
  };

  return (
    <div className="space-y-4">
      {/* Status geral */}
      <Card className={totalIssues === 0 ? 'border-emerald-500/50' : 'border-amber-500/50'}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {totalIssues === 0 ? (
                <CheckCircle className="h-6 w-6 text-emerald-500" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              )}
              <div>
                <CardTitle>
                  {totalIssues === 0 
                    ? 'Sistema Saudável' 
                    : `${totalIssues} Problema${totalIssues > 1 ? 's' : ''} Detectado${totalIssues > 1 ? 's' : ''}`
                  }
                </CardTitle>
                <CardDescription>
                  {diagnostics?.checked_at 
                    ? `Última verificação: ${new Date(diagnostics.checked_at).toLocaleString('pt-BR')}`
                    : 'Verificação automática'
                  }
                </CardDescription>
              </div>
            </div>
            {onRefreshData && (
              <Button variant="outline" size="sm" onClick={onRefreshData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar
              </Button>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Cards de diagnóstico */}
      <div className="grid gap-4 md:grid-cols-3">
        <DiagnosticCard
          title="Add-ons sem Stripe ID"
          description="Add-ons ativos que não possuem referência ao Stripe, possivelmente criados manualmente ou com falha na integração."
          count={diagnostics?.addons_without_stripe ?? 0}
          severity={(diagnostics?.addons_without_stripe ?? 0) > 0 ? 'warning' : 'success'}
          icon={(diagnostics?.addons_without_stripe ?? 0) > 0 ? AlertCircle : CheckCircle}
          actionLabel="Cadastrar Manualmente"
          actionIcon={Plus}
          onAction={handleAddonsWithoutStripe}
        />

        <DiagnosticCard
          title="Instâncias Órfãs"
          description="Provider instances pendentes há mais de 7 dias, podem indicar problemas no fluxo de ativação."
          count={diagnostics?.orphan_instances ?? 0}
          severity={(diagnostics?.orphan_instances ?? 0) > 0 ? 'error' : 'success'}
          icon={(diagnostics?.orphan_instances ?? 0) > 0 ? XCircle : CheckCircle}
          actionLabel="Ver Instâncias Pendentes"
          actionIcon={RefreshCw}
          onAction={handleOrphanInstances}
        />

        <DiagnosticCard
          title="Cancelados com Instâncias Ativas"
          description="Add-ons cancelados que ainda possuem instâncias ativas ou pendentes associadas."
          count={diagnostics?.cancelled_with_active_instances ?? 0}
          severity={(diagnostics?.cancelled_with_active_instances ?? 0) > 0 ? 'error' : 'success'}
          icon={(diagnostics?.cancelled_with_active_instances ?? 0) > 0 ? AlertTriangle : CheckCircle}
          actionLabel="Limpar Instâncias"
          actionIcon={Trash2}
          onAction={handleCancelledWithActive}
        />
      </div>

      {/* Mensagem quando não há problemas */}
      {totalIssues === 0 && (
        <Card className="bg-emerald-500/5 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
              <h3 className="font-medium text-lg">Nenhum problema detectado</h3>
              <p className="text-muted-foreground">
                Todos os add-ons e instâncias estão corretamente configurados.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
